/**
 * World invariants.
 *
 * These are the properties a generated map must have for the rest of the game to be
 * able to assume anything. They run in the tests over many seeds, and in the map
 * tool over every batch, so a parameter change that quietly starts producing broken
 * worlds is caught by the same command that renders them.
 *
 * Each problem is a sentence naming the tile, because "invalid map" is useless and
 * "tile (43, 12): forest on desert, which forest does not allow" is a fix.
 */

import type { Content } from '../../../content/schema/index.js';
import { indexToOffset, neighborsByDirection, oppositeDirection } from '../state/grid.js';
import type { TileGrid } from '../state/types.js';
import { findRegions } from './hydrology.js';

export interface ValidationProblem {
  /** Machine-readable kind, so tests can assert on a class of problem. */
  kind: string;
  message: string;
}

const NEIGHBOURS = new Int32Array(6);

export function validateWorld(grid: TileGrid, content: Content): ValidationProblem[] {
  const problems: ValidationProblem[] = [];
  const tiles = grid.terrain.length;
  const where = (i: number): string => {
    const { col, row } = indexToOffset(grid, i);
    return `(${col}, ${row})`;
  };
  const report = (kind: string, message: string): void => {
    // Cap the noise: twenty examples of the same broken rule is enough to diagnose it.
    if (problems.filter((p) => p.kind === kind).length < 20) problems.push({ kind, message });
  };

  const waterIds = new Set(content.terrain.all.filter((t) => t.water).map((t) => t.id));
  const isWater = (i: number): boolean => waterIds.has(grid.terrain[i]!);

  // ── content ids are real ──────────────────────────────────────────────────────
  for (let i = 0; i < tiles; i++) {
    if (grid.terrain[i]! >= content.terrain.all.length) {
      report('bad-terrain-id', `Tile ${where(i)} has terrain id ${grid.terrain[i]}, which does not exist.`);
    }
    if (grid.feature[i]! >= content.features.all.length) {
      report('bad-feature-id', `Tile ${where(i)} has feature id ${grid.feature[i]}, which does not exist.`);
    }
    if (grid.resource[i]! >= content.resources.all.length) {
      report('bad-resource-id', `Tile ${where(i)} has resource id ${grid.resource[i]}, which does not exist.`);
    }
  }
  if (problems.length > 0) return problems; // later checks would cascade meaninglessly

  // ── features and resources obey their own content rules ───────────────────────
  for (let i = 0; i < tiles; i++) {
    const terrain = content.terrain.get(grid.terrain[i]!);

    const feature = content.features.get(grid.feature[i]!);
    if (feature.key !== 'none' && !feature.validTerrain.includes(terrain.key)) {
      report(
        'feature-on-invalid-terrain',
        `Tile ${where(i)}: ${feature.key} on ${terrain.key}, which ${feature.key} does not allow.`,
      );
    }

    const resource = content.resources.get(grid.resource[i]!);
    if (resource.key !== 'none' && !resource.validTerrain.includes(terrain.key)) {
      report(
        'resource-on-invalid-terrain',
        `Tile ${where(i)}: ${resource.key} on ${terrain.key}, which ${resource.key} does not allow.`,
      );
    }
  }

  // ── poles are water, so no rule ever has to special-case the map edge ─────────
  for (let col = 0; col < grid.width; col++) {
    const top = col;
    const bottom = (grid.height - 1) * grid.width + col;
    if (!isWater(top)) report('land-at-pole', `Tile ${where(top)} is land on the northern map edge.`);
    if (!isWater(bottom)) report('land-at-pole', `Tile ${where(bottom)} is land on the southern map edge.`);
  }

  // ── coast is exactly the water that touches land ──────────────────────────────
  const coastId = content.terrain.id('coast');
  const oceanId = content.terrain.id('ocean');
  for (let i = 0; i < tiles; i++) {
    if (!isWater(i)) continue;
    const terrainId = grid.terrain[i]!;
    if (terrainId !== coastId && terrainId !== oceanId) continue; // lakes are exempt

    neighborsByDirection(grid, i, NEIGHBOURS);
    let touchesLand = false;
    for (let d = 0; d < 6; d++) {
      const n = NEIGHBOURS[d]!;
      if (n !== -1 && !isWater(n)) {
        touchesLand = true;
        break;
      }
    }
    if (touchesLand && terrainId === oceanId) {
      report('ocean-touching-land', `Tile ${where(i)} is deep ocean but sits against land; it should be coast.`);
    }
    if (!touchesLand && terrainId === coastId) {
      report('coast-without-land', `Tile ${where(i)} is coast but touches no land.`);
    }
  }

  // ── rivers are symmetric: if A flows to B, B records the link back to A ───────
  for (let i = 0; i < tiles; i++) {
    const mask = grid.rivers[i]!;
    if (mask === 0) continue;
    if (mask > 0x3f) {
      report('river-mask-overflow', `Tile ${where(i)} has river mask ${mask}, beyond the six valid directions.`);
      continue;
    }
    neighborsByDirection(grid, i, NEIGHBOURS);
    for (let d = 0; d < 6; d++) {
      if ((mask & (1 << d)) === 0) continue;
      const n = NEIGHBOURS[d]!;
      if (n === -1) {
        report('river-off-map', `Tile ${where(i)} has a river running off the edge of the map.`);
        continue;
      }
      if ((grid.rivers[n]! & (1 << oppositeDirection(d))) === 0) {
        report(
          'river-not-symmetric',
          `Tile ${where(i)} links a river to ${where(n)}, but ${where(n)} does not link back.`,
        );
      }
    }
  }

  // ── the map has usable land ───────────────────────────────────────────────────
  const landMask = new Uint8Array(tiles);
  for (let i = 0; i < tiles; i++) landMask[i] = isWater(i) ? 0 : 1;

  let landTiles = 0;
  for (let i = 0; i < tiles; i++) landTiles += landMask[i]!;
  const landFraction = landTiles / tiles;
  if (landFraction < 0.12) {
    report('too-little-land', `Only ${(landFraction * 100).toFixed(1)}% of the map is land; a game needs more.`);
  }
  if (landFraction > 0.75) {
    report('too-much-land', `${(landFraction * 100).toFixed(1)}% of the map is land; there is almost no sea.`);
  }

  const { sizes } = findRegions(grid, landMask, 1);
  const largest = sizes.length > 0 ? Math.max(...sizes) : 0;
  if (largest < 20) {
    report('no-continent', `The largest landmass is only ${largest} tiles; there is nowhere to build an empire.`);
  }

  // ── every strategic resource exists somewhere ─────────────────────────────────
  const seen = new Set<number>();
  for (let i = 0; i < tiles; i++) if (grid.resource[i] !== 0) seen.add(grid.resource[i]!);
  for (const resource of content.resources.all) {
    if (resource.class !== 'strategic' || resource.key === 'none') continue;
    if (!seen.has(resource.id)) {
      report('missing-strategic', `No ${resource.key} anywhere on the map; a strategic resource must exist.`);
    }
  }

  return problems;
}

/** Throw if the world is invalid, with every problem listed. For tests and tools. */
export function assertValidWorld(grid: TileGrid, content: Content): void {
  const problems = validateWorld(grid, content);
  if (problems.length === 0) return;
  throw new Error(`Generated world failed validation:\n${problems.map((p) => `  • ${p.message}`).join('\n')}`);
}
